import React, { useState, useEffect } from "react";
import { analyzeUrl, validateUrl, getMetricStatus, calculateAdNetworkRatio } from "../../services/adQuality";
import Loader from "../../components/misc/loader";
import Wrapper from "../../components/dashboard/wrapper";
import GaugeComponent from 'react-gauge-component';
import checkmarkGreen from "../../assets/svgs/checkmark-green.svg";
import errorIcon from "../../assets/svgs/error.svg";
import arcspanLogo from "../../assets/svgs/arcspan-logo.svg";
import "./index.scss";

function AdQualityAnalyzer() {
	const [url, setUrl] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [results, setResults] = useState(null);
	const [showAdRatioBreakdown, setShowAdRatioBreakdown] = useState(false);
	const [showUrlChanges, setShowUrlChanges] = useState(false);
	const [showIntrusiveAds, setShowIntrusiveAds] = useState(false);
	const [showVideoIssues, setShowVideoIssues] = useState(false);
	const [debugMode, setDebugMode] = useState(false);
	const [rawResponse, setRawResponse] = useState(null);

	// Check for test mode via query parameter
	const isTestMode = new URLSearchParams(window.location.search).get('test') === 'video';

	// Handle ESC key to close debug overlay
	useEffect(() => {
		const handleKeyDown = (event) => {
			if (event.key === 'Escape' && debugMode) {
				setDebugMode(false);
			}
		};

		if (debugMode) {
			document.addEventListener('keydown', handleKeyDown);
			// Prevent body scrolling when overlay is open
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = 'unset';
		}

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			document.body.style.overflow = 'unset';
		};
	}, [debugMode]);

	const getAdSlotsStatus = (totalAdSlots) => {
		if (totalAdSlots <= 8) return { status: 'good', label: 'Good' };
		if (totalAdSlots <= 15) return { status: 'warning', label: 'OK' };
		return { status: 'critical', label: 'Bad' };
	};

	const getAdRatioStatus = (ratio) => {
		if (ratio <= 0.30) return { status: 'good', label: 'Good' };
		if (ratio <= 0.40) return { status: 'warning', label: 'Warning' };
		return { status: 'critical', label: 'Critical' };
	};

	const getOutOfViewStatus = (ratio) => {
		if (ratio <= 0.30) return { status: 'good', label: 'Good' };
		if (ratio <= 0.50) return { status: 'warning', label: 'Warning' };
		return { status: 'critical', label: 'Critical' };
	};

	const getNetworkRequestsStatus = (percentage) => {
		if (percentage <= 20) return { status: 'good', label: 'Good' };
		if (percentage <= 35) return { status: 'warning', label: 'Warning' };
		return { status: 'critical', label: 'Critical' };
	};

	const getTotalNetworkRequestsStatus = (totalRequests) => {
		if (totalRequests <= 75) return { status: 'good', label: 'Good' };
		if (totalRequests <= 120) return { status: 'warning', label: 'Warning' };
		return { status: 'critical', label: 'Critical' };
	};

	const getRefreshRateStatus = (refreshRate) => {
		// Refresh rate is in seconds (average time between ad refreshes)
		// Good: ≥30 seconds (infrequent refreshes)
		// Warning: 10-29 seconds (moderate refreshes)  
		// Critical: <10 seconds (aggressive refreshes)
		if (refreshRate === 0) return { status: 'good', label: 'No Refresh' };
		if (refreshRate >= 30) return { status: 'good', label: 'Good' };
		if (refreshRate >= 10) return { status: 'warning', label: 'Warning' };
		return { status: 'critical', label: 'Critical' };
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		
		if (!validateUrl(url)) {
			setError("Please enter a valid URL starting with http:// or https://");
			return;
		}

		setError(null);
		setLoading(true);
		setResults(null);

		try {
			const response = await analyzeUrl(url);
			
			// Store raw response for debug mode
			setRawResponse(response);
			
			if (response && response.body) {
				// Merge top-level fields with body for easy access
				let processedResults = {
					...response.body,
					source: response.source
				};
				
				// Inject test video data if in test mode
				if (isTestMode) {
					processedResults.videoMetrics = {
						totalVideos: 4,
						playingVideos: 3,
						avgPlayDuration: 15.5,
						overlappingVideos: 2
					};
					processedResults.mutedVideoRatio = 0.5; // 50% muted, 50% unmuted
					processedResults.videoAdOverlaps = 2;
					console.log('Test mode active - injected video data:', processedResults.videoMetrics);
				}
				
				setResults(processedResults);
			} else {
				setError("Invalid response format from API");
			}
		} catch (err) {
			setError(err.message || "Failed to analyze URL. Please try again.");
		} finally {
			setLoading(false);
		}
	};


	const renderAdSlotsGauge = () => {
		const totalAdSlots = results.totalAdSlots;
		const adSlotsInfo = getAdSlotsStatus(totalAdSlots);
		
		// Calculate gauge value (0-100 scale, where 20+ is max)
		const gaugeValue = Math.min((totalAdSlots / 20) * 100, 100);
		
		return (
			<div className="ad-slots-gauge-card">
				<div className="metric-card__header">
					<div className="metric-card__title p4">
						Total Ad Slots
						<div className="tooltip">
							<div className="tooltip-title">Total Ad Slots</div>
							<div className="tooltip-definition">
								The total number of advertising positions or placements available on the webpage, including display ads, video ads, and other promotional content areas.
							</div>
							<div className="tooltip-standards">
								<div className="standard-item"><strong>Good:</strong> 1-8 slots - Maintains excellent user experience</div>
								<div className="standard-item"><strong>Warning:</strong> 9-15 slots - Acceptable but monitor viewability</div>
								<div className="standard-item"><strong>Critical:</strong> 16+ slots - May negatively impact user experience</div>
							</div>
						</div>
					</div>
				</div>
				<div className="metric-card__value h2">{totalAdSlots}</div>
				<div className="gauge-container">
					<GaugeComponent
						type="semicircle"
						arc={{
							colorArray: ['#28a745', '#ffc107', '#dc3545'],
							padding: 0.02,
							subArcs: [
								{ limit: 40 }, // 0-8 slots (good)
								{ limit: 75 }, // 9-15 slots (warning)  
								{ limit: 100 } // 16+ slots (critical)
							]
						}}
						pointer={{
							color: '#345243',
							length: 0.80,
							width: 15,
						}}
						labels={{
							valueLabel: { 
								formatTextValue: () => '',  // Hide the center text
							},
							tickLabels: {
								type: 'outer',
								ticks: [
									{ value: 0 },
									{ value: 40, valueConfig: { formatTextValue: () => '8' }},
									{ value: 75, valueConfig: { formatTextValue: () => '15' }},
									{ value: 100, valueConfig: { formatTextValue: () => '20+' }}
								]
							}
						}}
						value={gaugeValue}
						minValue={0}
						maxValue={100}
					/>
				</div>
				<div className={`metric-card__subtext p4 ad-slots-status ${adSlotsInfo.status}`}>
					{adSlotsInfo.label} - {totalAdSlots <= 8 ? 'Excellent user experience' : 
					 totalAdSlots <= 15 ? 'Acceptable, monitor viewability' : 
					 'May impact user experience'}
				</div>
			</div>
		);
	};

	const renderAdRatioGauge = () => {
		const adRatio = results.adToContentRatio.overall;
		const adRatioInfo = getAdRatioStatus(adRatio);
		
		// Calculate gauge value (0-100 scale) to match the visual zones
		// Map percentage to gauge scale: 30% = 60, 40% = 80, 50% = 100
		const gaugeValue = Math.min((adRatio * 100) * 2, 100);
		
		return (
			<div className="ad-slots-gauge-card prominent">
				<div className="metric-card__header">
					<div className="metric-card__title p4">
						Overall Ad Ratio
						<div className="tooltip">
							<div className="tooltip-title">Overall Ad Ratio</div>
							<div className="tooltip-definition">
								The percentage of webpage content that consists of advertising compared to editorial content. This measures the balance between ads and actual page content.
							</div>
							<div className="tooltip-standards">
								<div className="standard-item"><strong>Good:</strong> ≤30% - Meets industry standards (Google/IAB recommended)</div>
								<div className="standard-item"><strong>Warning:</strong> 31-40% - Approaching problematic levels</div>
								<div className="standard-item"><strong>Critical:</strong> &gt;40% - Significantly impacts user experience and SEO</div>
							</div>
						</div>
					</div>
				</div>
				<div className="metric-card__value h2">{(adRatio * 100).toFixed(1)}%</div>
				<div className="gauge-container">
					<GaugeComponent
						type="semicircle"
						arc={{
							colorArray: ['#28a745', '#ffc107', '#dc3545'],
							padding: 0.02,
							subArcs: [
								{ limit: 60 }, // 0-30% (good - industry standard)
								{ limit: 80 }, // 31-40% (warning)  
								{ limit: 100 } // 41-50%+ (critical)
							]
						}}
						pointer={{
							color: '#345243',
							length: 0.80,
							width: 15,
						}}
						labels={{
							valueLabel: { 
								formatTextValue: () => '',  // Hide the center text
							},
							tickLabels: {
								type: 'outer',
								ticks: [
									{ value: 0, valueConfig: { formatTextValue: () => '0%' }},
									{ value: 60, valueConfig: { formatTextValue: () => '30%' }},
									{ value: 80, valueConfig: { formatTextValue: () => '40%' }},
									{ value: 100, valueConfig: { formatTextValue: () => '50%' }}
								]
							}
						}}
						value={gaugeValue}
						minValue={0}
						maxValue={100}
					/>
				</div>
				<div className={`metric-card__subtext p4 ad-slots-status ${adRatioInfo.status}`}>
					{adRatioInfo.label} - {adRatio <= 0.30 ? 'Meets Google/IAB standards' : 
					 adRatio <= 0.40 ? 'Approaching problematic levels' : 
					 'Violates industry standards'}
					<span className="impact-message">
						{adRatio <= 0.30 ? 'Maintains advertiser trust and SEO ranking' : 
						 adRatio <= 0.40 ? 'May impact user experience and SEO' : 
						 'Risk of advertiser penalties and poor SEO'}
					</span>
					<button 
						className="breakdown-button p4"
						onClick={() => setShowAdRatioBreakdown(!showAdRatioBreakdown)}
					>
						{showAdRatioBreakdown ? '▼ Hide' : '▶ View'} section breakdown
					</button>
				</div>
				
				{/* Conditional section breakdown */}
				{showAdRatioBreakdown && (
					<div className="breakdown-section">
						{renderAdRatioSection()}
					</div>
				)}
			</div>
		);
	};

	const renderOutOfViewGauge = () => {
		const outOfViewRatio = results.outOfViewAdRequestRatio;
		const outOfViewInfo = getOutOfViewStatus(outOfViewRatio);
		
		// Calculate gauge value (0-100 scale) to match the visual zones
		// Map percentage to gauge scale: 30% = 60, 50% = 100
		const gaugeValue = Math.min((outOfViewRatio * 100) * 2, 100);
		
		return (
			<div className="ad-slots-gauge-card prominent">
				<div className="metric-card__header">
					<div className="metric-card__title p4">
						Out-of-View Ad Ratio
						<div className="tooltip">
							<div className="tooltip-title">Out-of-View Ad Ratio</div>
							<div className="tooltip-definition">
								The percentage of ads that fail to meet viewability standards (50% visible for 1+ seconds for display, 2+ seconds for video). Lower ratios indicate better ad performance and revenue potential.
							</div>
							<div className="tooltip-standards">
								<div className="standard-item"><strong>Good:</strong> ≤30% - Excellent viewability (70%+ viewable)</div>
								<div className="standard-item"><strong>Warning:</strong> 31-50% - Industry minimum threshold</div>
								<div className="standard-item"><strong>Critical:</strong> &gt;50% - Advertisers may reduce bids or stop buying</div>
							</div>
						</div>
					</div>
				</div>
				<div className="metric-card__value h2">{(outOfViewRatio * 100).toFixed(1)}%</div>
				<div className="gauge-container">
					<GaugeComponent
						type="semicircle"
						arc={{
							colorArray: ['#28a745', '#ffc107', '#dc3545'],
							padding: 0.02,
							subArcs: [
								{ limit: 60 }, // 0-30% (good - industry standard)
								{ limit: 100 } // 31-50%+ (warning to critical)  
							]
						}}
						pointer={{
							color: '#345243',
							length: 0.80,
							width: 15,
						}}
						labels={{
							valueLabel: { 
								formatTextValue: () => '',  // Hide the center text
							},
							tickLabels: {
								type: 'outer',
								ticks: [
									{ value: 0, valueConfig: { formatTextValue: () => '0%' }},
									{ value: 60, valueConfig: { formatTextValue: () => '30%' }},
									{ value: 100, valueConfig: { formatTextValue: () => '50%' }}
								]
							}
						}}
						value={gaugeValue}
						minValue={0}
						maxValue={100}
					/>
				</div>
				<div className={`metric-card__subtext p4 ad-slots-status ${outOfViewInfo.status}`}>
					{outOfViewInfo.label} - {outOfViewRatio <= 0.30 ? 'Excellent viewability (70%+ visible)' : 
					 outOfViewRatio <= 0.50 ? 'Meeting minimum threshold' : 
					 'Poor viewability performance'}
					<span className="impact-message">
						{outOfViewRatio <= 0.30 ? 'Commands premium ad rates and higher revenue' : 
						 outOfViewRatio <= 0.50 ? 'Standard rates, room for revenue optimization' : 
						 'Advertisers will reduce bids or stop buying inventory'}
					</span>
				</div>
			</div>
		);
	};

	const renderNetworkRequestsGauge = () => {
		const networkPercentage = parseFloat(calculateAdNetworkRatio(results.adRelatedNetworkCalls, results.totalNetworkCalls));
		const networkInfo = getNetworkRequestsStatus(networkPercentage);
		
		// Calculate gauge value (0-100 scale) to match the visual zones
		// Map percentage to gauge scale: 20% = 40, 35% = 70, 50% = 100
		const gaugeValue = Math.min((networkPercentage / 50) * 100, 100);
		
		return (
			<div className="ad-slots-gauge-card">
				<div className="metric-card__header">
					<div className="metric-card__title p4">
						Ad Network Requests
						<div className="tooltip">
							<div className="tooltip-title">Ad Network Requests</div>
							<div className="tooltip-definition">
								The percentage of HTTP requests that are ad-related (ads, tracking, analytics) out of all network requests made by the webpage. Lower ratios indicate better performance.
							</div>
							<div className="tooltip-standards">
								<div className="standard-item"><strong>Good:</strong> ≤20% - Minimal performance impact, well-optimized</div>
								<div className="standard-item"><strong>Warning:</strong> 21-35% - Moderate impact, monitor page load times</div>
								<div className="standard-item"><strong>Critical:</strong> &gt;35% - Significant performance degradation</div>
							</div>
						</div>
					</div>
				</div>
				<div className="metric-card__value h2">{networkPercentage.toFixed(1)}%</div>
				<div className="gauge-container">
					<GaugeComponent
						type="semicircle"
						arc={{
							colorArray: ['#28a745', '#ffc107', '#dc3545'],
							padding: 0.02,
							subArcs: [
								{ limit: 40 }, // 0-20% (good)
								{ limit: 70 }, // 21-35% (warning)  
								{ limit: 100 } // 36%+ (critical)
							]
						}}
						pointer={{
							color: '#345243',
							length: 0.80,
							width: 15,
						}}
						labels={{
							valueLabel: { 
								formatTextValue: () => '',  // Hide the center text
							},
							tickLabels: {
								type: 'outer',
								ticks: [
									{ value: 0, valueConfig: { formatTextValue: () => '0%' }},
									{ value: 40, valueConfig: { formatTextValue: () => '20%' }},
									{ value: 70, valueConfig: { formatTextValue: () => '35%' }},
									{ value: 100, valueConfig: { formatTextValue: () => '50%' }}
								]
							}
						}}
						value={gaugeValue}
						minValue={0}
						maxValue={100}
					/>
				</div>
				<div className={`metric-card__subtext p4 ad-slots-status ${networkInfo.status}`}>
					{networkInfo.label} - {results.adRelatedNetworkCalls} of {results.totalNetworkCalls} requests
				</div>
			</div>
		);
	};

	const renderTotalNetworkRequestsGauge = () => {
		const totalRequests = results.totalNetworkCalls;
		const requestsInfo = getTotalNetworkRequestsStatus(totalRequests);
		
		// Calculate gauge value (0-100 scale) to match the visual zones
		// Map total requests: 75 = 50, 120 = 75, 200 = 100
		const gaugeValue = Math.min((totalRequests / 200) * 100, 100);
		
		return (
			<div className="ad-slots-gauge-card">
				<div className="metric-card__header">
					<div className="metric-card__title p4">
						Total Network Requests
						<div className="tooltip tooltip-right">
							<div className="tooltip-title">Total Network Requests</div>
							<div className="tooltip-definition">
								The total number of HTTP requests made by the webpage (all resources: HTML, CSS, JS, images, ads, analytics, etc.). Based on 2024 HTTP Archive data for desktop performance.
							</div>
							<div className="tooltip-standards">
								<div className="standard-item"><strong>Good:</strong> ≤75 requests - At/below median (50th percentile)</div>
								<div className="standard-item"><strong>Warning:</strong> 76-120 requests - Above median but common (50th-75th percentile)</div>
								<div className="standard-item"><strong>Critical:</strong> &gt;120 requests - Top 25% of sites, likely performance impact</div>
							</div>
						</div>
					</div>
				</div>
				<div className="metric-card__value h2">{totalRequests}</div>
				<div className="gauge-container">
					<GaugeComponent
						type="semicircle"
						arc={{
							colorArray: ['#28a745', '#ffc107', '#dc3545'],
							padding: 0.02,
							subArcs: [
								{ limit: 37.5 }, // 0-75 requests (good - at/below median)
								{ limit: 60 }, // 76-120 requests (warning - 50th-75th percentile)  
								{ limit: 100 } // 121+ requests (critical - top 25%)
							]
						}}
						pointer={{
							color: '#345243',
							length: 0.80,
							width: 15,
						}}
						labels={{
							valueLabel: { 
								formatTextValue: () => '',  // Hide the center text
							},
							tickLabels: {
								type: 'outer',
								ticks: [
									{ value: 0, valueConfig: { formatTextValue: () => '0' }},
									{ value: 37.5, valueConfig: { formatTextValue: () => '75' }},
									{ value: 60, valueConfig: { formatTextValue: () => '120' }},
									{ value: 100, valueConfig: { formatTextValue: () => '200+' }}
								]
							}
						}}
						value={gaugeValue}
						minValue={0}
						maxValue={100}
					/>
				</div>
				<div className={`metric-card__subtext p4 ad-slots-status ${requestsInfo.status}`}>
					{requestsInfo.label} - {totalRequests <= 75 ? 'At or below web median performance' : 
					 totalRequests <= 120 ? 'Above median but reasonable for commercial sites' : 
					 'High request count may impact page performance'}
				</div>
			</div>
		);
	};

	const renderRefreshRateGauge = () => {
		const refreshRate = results.averageAdRefreshRate;
		const refreshInfo = getRefreshRateStatus(refreshRate);
		
		// Calculate gauge value (0-100 scale) to match the visual zones
		// Map refresh rate: 60+ seconds = 0-25, 30-59s = 25-50, 10-29s = 50-75, <10s = 75-100
		let gaugeValue;
		if (refreshRate === 0) {
			gaugeValue = 0; // No refresh = best case
		} else if (refreshRate >= 60) {
			gaugeValue = 25; // Very good
		} else if (refreshRate >= 30) {
			gaugeValue = 40; // Good
		} else if (refreshRate >= 10) {
			gaugeValue = 70; // Warning
		} else {
			gaugeValue = 95; // Critical
		}
		
		return (
			<div className="ad-slots-gauge-card">
				<div className="metric-card__header">
					<div className="metric-card__title p4">
						Ad Refresh Rate
						<div className="tooltip">
							<div className="tooltip-title">Ad Refresh Rate</div>
							<div className="tooltip-definition">
								Average time between ad refreshes on the page (measured in seconds). Frequent refreshing can hurt user experience and ad viewability.
							</div>
							<div className="tooltip-standards">
								<div className="standard-item"><strong>Good:</strong> ≥30 seconds or no refresh - Industry best practice</div>
								<div className="standard-item"><strong>Warning:</strong> 10-29 seconds - Moderate refreshing</div>
								<div className="standard-item"><strong>Critical:</strong> &lt;10 seconds - Aggressive refreshing hurts UX</div>
							</div>
						</div>
					</div>
				</div>
				<div className="metric-card__value h2">
					{refreshRate > 0 ? (refreshRate < 0.1 ? `${refreshRate.toFixed(2)}s` : `${refreshRate.toFixed(1)}s`) : 'No refresh'}
				</div>
				<div className="gauge-container">
					<GaugeComponent
						type="semicircle"
						arc={{
							colorArray: ['#28a745', '#ffc107', '#dc3545'],
							padding: 0.02,
							subArcs: [
								{ limit: 50 }, // No refresh or ≥30s (good)
								{ limit: 75 }, // 10-29s (warning)  
								{ limit: 100 } // <10s (critical)
							]
						}}
						pointer={{
							color: '#345243',
							length: 0.80,
							width: 15,
						}}
						labels={{
							valueLabel: { 
								formatTextValue: () => '',  // Hide the center text
							},
							tickLabels: {
								type: 'outer',
								ticks: [
									{ value: 0, valueConfig: { formatTextValue: () => '∞' }},
									{ value: 50, valueConfig: { formatTextValue: () => '30s' }},
									{ value: 75, valueConfig: { formatTextValue: () => '10s' }},
									{ value: 100, valueConfig: { formatTextValue: () => '0s' }}
								]
							}
						}}
						value={gaugeValue}
						minValue={0}
						maxValue={100}
					/>
				</div>
				<div className={`metric-card__subtext p4 ad-slots-status ${refreshInfo.status}`}>
					{refreshInfo.label} - {refreshRate > 0 ? `Average refresh interval: ${refreshRate < 0.1 ? refreshRate.toFixed(2) : refreshRate.toFixed(1)}s` : 'Ads do not refresh automatically'}
				</div>
			</div>
		);
	};

	const renderAdRatioSection = () => {
		const { adToContentRatio } = results;
		const sections = [
			{ name: 'Top Section', ratio: adToContentRatio.topSection },
			{ name: 'Middle Section', ratio: adToContentRatio.middleSection },
			{ name: 'Bottom Section', ratio: adToContentRatio.bottomSection }
		];
		
		return (
			<div className="section-breakdown">
				<h4 className="breakdown-title">Section Breakdown</h4>
				<div className="section-breakdown-grid">
					{sections.map((section, index) => {
						const percentage = (section.ratio * 100).toFixed(1);
						const status = getMetricStatus(section.ratio);
						
						return (
							<div key={index} className="section-item">
								<div className="section-header">
									<span className="section-name">{section.name}</span>
									<span className={`section-value ${status}`}>{percentage}%</span>
								</div>
								<div className="section-bar">
									<div 
										className={`section-fill ${status}`}
										style={{ width: `${Math.min(section.ratio * 100, 100)}%` }}
									/>
								</div>
							</div>
						);
					})}
				</div>
			</div>
		);
	};

	const renderSummaryCard = () => {
		// Calculate overall quality score based on ALL metrics
		const adRatio = results.adToContentRatio.overall;
		const outOfViewRatio = results.outOfViewAdRequestRatio;
		const totalSlots = results.totalAdSlots;
		const hasIntrusiveAds = Object.values(results.intrusiveAdsDetected).some(detected => detected);
		const networkPercentage = parseFloat(calculateAdNetworkRatio(results.adRelatedNetworkCalls, results.totalNetworkCalls));
		const totalRequests = results.totalNetworkCalls;
		const refreshRate = results.averageAdRefreshRate;
		
		// Scoring system (0-100)
		let score = 100;
		let penalties = [];
		
		// Ad ratio penalties (max -25)
		if (adRatio > 0.40) {
			score -= 25;
			penalties.push({ category: 'Ad Density', points: -25, reason: `${(adRatio * 100).toFixed(1)}% ad ratio (critical)` });
		} else if (adRatio > 0.30) {
			score -= 12;
			penalties.push({ category: 'Ad Density', points: -12, reason: `${(adRatio * 100).toFixed(1)}% ad ratio (warning)` });
		}
		
		// Viewability penalties (max -20)
		if (outOfViewRatio > 0.50) {
			score -= 20;
			penalties.push({ category: 'Viewability', points: -20, reason: `${(outOfViewRatio * 100).toFixed(1)}% ads out-of-view` });
		} else if (outOfViewRatio > 0.30) {
			score -= 10;
			penalties.push({ category: 'Viewability', points: -10, reason: `${(outOfViewRatio * 100).toFixed(1)}% ads out-of-view` });
		}
		
		// Ad slots penalties (max -10)
		if (totalSlots > 15) {
			score -= 10;
			penalties.push({ category: 'Ad Slots', points: -10, reason: `${totalSlots} total ad slots (excessive)` });
		} else if (totalSlots > 8) {
			score -= 5;
			penalties.push({ category: 'Ad Slots', points: -5, reason: `${totalSlots} total ad slots` });
		}
		
		// Network performance penalties (max -15)
		if (networkPercentage > 35) {
			score -= 10;
			penalties.push({ category: 'Network', points: -10, reason: `${networkPercentage.toFixed(1)}% ad-related requests` });
		} else if (networkPercentage > 20) {
			score -= 5;
			penalties.push({ category: 'Network', points: -5, reason: `${networkPercentage.toFixed(1)}% ad-related requests` });
		}
		
		if (totalRequests > 120) {
			score -= 5;
			penalties.push({ category: 'Performance', points: -5, reason: `${totalRequests} total requests (heavy)` });
		}
		
		// Video issues penalties (max -15)
		if (results.videoMetrics) {
			const vm = results.videoMetrics;
			if (vm.playingVideos > 1) {
				score -= 8;
				penalties.push({ category: 'Video', points: -8, reason: `${vm.playingVideos} videos playing simultaneously` });
			}
			if (vm.overlappingVideos > 0) {
				score -= 5;
				penalties.push({ category: 'Video', points: -5, reason: `${vm.overlappingVideos} overlapping videos` });
			}
			if (results.videoAdOverlaps > 0) {
				score -= 7;
				penalties.push({ category: 'Video Ads', points: -7, reason: `${results.videoAdOverlaps} video ad overlaps` });
			}
			// Penalty for unmuted autoplay videos
			if (vm.playingVideos > 0 && results.mutedVideoRatio < 1.0) {
				const unmutedCount = Math.round(vm.playingVideos * (1 - results.mutedVideoRatio));
				if (unmutedCount > 0) {
					score -= 5;
					penalties.push({ category: 'Video', points: -5, reason: `${unmutedCount} unmuted autoplay video(s)` });
				}
			}
		}
		
		// Intrusive ads penalty (max -15)
		if (hasIntrusiveAds) {
			score -= 15;
			const intrusiveTypes = [];
			if (results.intrusiveAdsDetected.popups) intrusiveTypes.push("pop-ups");
			if (results.intrusiveAdsDetected.autoplayVideo) intrusiveTypes.push("autoplay videos");
			if (results.intrusiveAdsDetected.sticky) intrusiveTypes.push("sticky overlays");
			if (results.intrusiveAdsDetected.interstitials) intrusiveTypes.push("interstitials");
			if (results.intrusiveAdsDetected.expandable) intrusiveTypes.push("expandable ads");
			if (results.intrusiveAdsDetected.overlays) intrusiveTypes.push("overlays");
			penalties.push({ category: 'Intrusive Ads', points: -15, reason: intrusiveTypes.join(", ") });
		}
		
		// URL changes penalty
		if (results.dynamicUrlChangeDetected) {
			score -= 5;
			penalties.push({ category: 'Tracking', points: -5, reason: 'Dynamic URL changes detected' });
		}
		
		// Infinite scroll penalty
		if (results.infiniteScrollDetected) {
			score -= 3;
			penalties.push({ category: 'Page Structure', points: -3, reason: 'Infinite scroll detected' });
		}
		
		// Ad refresh rate penalties (max -10)
		if (refreshRate > 0 && refreshRate < 10) {
			score -= 10;
			penalties.push({ category: 'Refresh Rate', points: -10, reason: `Every ${refreshRate.toFixed(1)}s (aggressive)` });
		} else if (refreshRate > 0 && refreshRate < 30) {
			score -= 5;
			penalties.push({ category: 'Refresh Rate', points: -5, reason: `Every ${refreshRate.toFixed(1)}s` });
		}
		
		score = Math.max(0, score);
		
		// Determine grade and status
		let grade, status, message;
		if (score >= 90) {
			grade = 'A';
			status = 'excellent';
			message = 'Exceptional ad quality with excellent user experience';
		} else if (score >= 80) {
			grade = 'B';
			status = 'good';
			message = 'Good ad quality with minor optimization opportunities';
		} else if (score >= 70) {
			grade = 'C';
			status = 'fair';
			message = 'Fair ad quality with several areas for improvement';
		} else if (score >= 60) {
			grade = 'D';
			status = 'poor';
			message = 'Poor ad quality requiring significant optimization';
		} else {
			grade = 'F';
			status = 'critical';
			message = 'Critical ad quality issues need immediate attention';
		}
		
		return (
			<div className="summary-card">
				<div className="summary-header">
					<div className="summary-title">
						<h2 className="h3">Quality Cloud™ Analysis</h2>
						<div className="summary-meta">
							<span className="p4">URL: {results.url}</span>
							<span className="p4">Analyzed: {new Date(results.timestamp).toLocaleString()}</span>
							<span className="p4">Source: <span className={`badge ${results.source === 'cache' ? 'cached' : 'fresh'}`}>{results.source}</span></span>
						</div>
					</div>
					<div className="summary-score">
						<div className={`grade-circle ${status}`}>
							<span className="grade-letter">{grade}</span>
							<div className="scoring-tooltip">
								<div className="tooltip-title">ArcScore™ Methodology</div>
								<div className="scoring-breakdown">
									{penalties.length > 0 && (
										<div className="scoring-section">
											<strong>Current Page Penalties:</strong>
											{penalties.map((penalty, index) => (
												<div key={index} className="scoring-item penalty-item">
													<span className="penalty-category">{penalty.category}:</span>
													<span className="penalty-points">{penalty.points}</span>
													<span className="penalty-reason">{penalty.reason}</span>
												</div>
											))}
											<div className="scoring-item total-score">
												<strong>Final Score: {score}/100</strong>
											</div>
										</div>
									)}
									<div className="scoring-section">
										<strong>All Scoring Categories:</strong>
										<div className="scoring-item">• Ad Density: Up to -25 points</div>
										<div className="scoring-item">• Viewability: Up to -20 points</div>
										<div className="scoring-item">• Intrusive Ads: Up to -15 points</div>
										<div className="scoring-item">• Video Ad Issues: Up to -15 points</div>
										<div className="scoring-item">• Network Performance: Up to -15 points</div>
										<div className="scoring-item">• Ad Slots Count: Up to -10 points</div>
										<div className="scoring-item">• Tracking/URL: Up to -5 points</div>
									</div>
									<div className="scoring-section">
										<strong>Grade Scale:</strong>
										<div className="grade-item">A (90-100): Exceptional quality</div>
										<div className="grade-item">B (80-89): Good, minor issues</div>
										<div className="grade-item">C (70-79): Fair, needs work</div>
										<div className="grade-item">D (60-69): Poor quality</div>
										<div className="grade-item">F (&lt;60): Critical issues</div>
									</div>
								</div>
							</div>
						</div>
						<div className="score-details">
							<div className="score-number">{score}/100</div>
							<div className="score-label">ArcScore</div>
						</div>
					</div>
				</div>
				<div className="summary-message">
					<span className="p3">{message}</span>
				</div>
			</div>
		);
	};

	const renderAdQualityChecks = () => {
		const hasIntrusiveAds = Object.values(results.intrusiveAdsDetected).some(detected => detected);
		const hasVideoIssues = results.videoMetrics && (
			results.videoMetrics.overlappingVideos > 0 || 
			results.videoAdOverlaps > 0 ||
			results.videoMetrics.playingVideos > 1 ||
			(results.videoMetrics.playingVideos > 0 && results.mutedVideoRatio < 1.0)
		);
		
		// Generate specific failure descriptions
		const getIntrusiveAdsDescription = () => {
			if (!hasIntrusiveAds) {
				return "Scanned for intrusive formats like pop-ups, autoplay videos, and sticky overlays";
			}
			
			return "Detected intrusive ad formats that may negatively impact user experience.";
		};
		
		const getUrlChangeDescription = () => {
			if (!results.dynamicUrlChangeDetected) {
				return "Checked for dynamic URL changes that can disrupt ad tracking and analytics";
			}
			
			const changeCount = results.urlChanges ? results.urlChanges.length : 1;
			return `Detected ${changeCount} URL ${changeCount === 1 ? 'change' : 'changes'} during analysis. This can disrupt ad tracking and analytics accuracy.`;
		};
		
		const renderUrlChangesList = () => {
			if (!results.urlChanges || results.urlChanges.length === 0) return null;
			
			return (
				<ul className="url-changes-list">
					{results.urlChanges.map((url, index) => (
						<li key={index} className="p4">{url}</li>
					))}
				</ul>
			);
		};
		
		const renderIntrusiveAdsList = () => {
			if (!results.intrusiveAdsDetected) return null;
			
			const detectedTypes = [];
			if (results.intrusiveAdsDetected.popups) detectedTypes.push("Pop-ups");
			if (results.intrusiveAdsDetected.autoplayVideo) detectedTypes.push("Autoplay videos");
			if (results.intrusiveAdsDetected.sticky) detectedTypes.push("Sticky overlays");
			if (results.intrusiveAdsDetected.interstitials) detectedTypes.push("Interstitials");
			if (results.intrusiveAdsDetected.expandable) detectedTypes.push("Expandable ads");
			if (results.intrusiveAdsDetected.overlays) detectedTypes.push("Overlays");
			
			if (detectedTypes.length === 0) return null;
			
			return (
				<ul className="intrusive-ads-list">
					{detectedTypes.map((type, index) => (
						<li key={index} className="p4">{type}</li>
					))}
				</ul>
			);
		};
		
		const getScrollDescription = () => {
			if (!results.infiniteScrollDetected) {
				return "Analyzed page structure for infinite scroll patterns that affect viewability measurement";
			}
			
			return "Detected infinite scroll functionality. This can complicate viewability measurement and may affect ad performance tracking.";
		};
		
		const getVideoQualityDescription = () => {
			if (!results.videoMetrics || results.videoMetrics.totalVideos === 0) {
				return "No video ads detected on page";
			}
			
			const avgDuration = results.videoMetrics.avgPlayDuration;
			const durationText = avgDuration > 0 ? ` (avg ${avgDuration.toFixed(1)}s)` : '';
			
			if (!hasVideoIssues) {
				return `${results.videoMetrics.totalVideos} video ad(s) detected${durationText} with proper implementation`;
			}
			
			// Include video count and duration in the description
			const videoCount = results.videoMetrics.totalVideos;
			const baseText = `${videoCount} video ad(s)${avgDuration > 0 ? ` (avg ${avgDuration.toFixed(1)}s)` : ''}`;
			
			// Count the number of issues for a simpler message
			let issueCount = 0;
			if (results.videoMetrics.playingVideos > 1) issueCount++;
			if (results.videoMetrics.overlappingVideos > 0) issueCount++;
			if (results.videoAdOverlaps > 0) issueCount++;
			if (results.videoMetrics.playingVideos > 0 && results.mutedVideoRatio < 1.0) {
				const unmutedCount = Math.round(results.videoMetrics.playingVideos * (1 - results.mutedVideoRatio));
				if (unmutedCount > 0) issueCount++;
			}
			
			return `${baseText} - ${issueCount} issue${issueCount !== 1 ? 's' : ''} detected`;
		};
		
		const renderVideoIssuesList = () => {
			if (!results.videoMetrics || !hasVideoIssues) return null;
			
			const issues = [];
			const vm = results.videoMetrics;
			
			if (vm.playingVideos > 1) {
				issues.push(`Multiple videos playing: ${vm.playingVideos} simultaneous videos`);
			}
			if (vm.overlappingVideos > 0) {
				issues.push(`Overlapping videos: ${vm.overlappingVideos} videos overlap content`);
			}
			if (results.videoAdOverlaps > 0) {
				issues.push(`Video ad overlaps: ${results.videoAdOverlaps} ads overlap other ads`);
			}
			if (vm.playingVideos > 0 && results.mutedVideoRatio < 1.0) {
				const unmutedCount = Math.round(vm.playingVideos * (1 - results.mutedVideoRatio));
				if (unmutedCount > 0) {
					issues.push(`Unmuted autoplay: ${unmutedCount} video(s) play with sound`);
				}
			}
			
			return (
				<ul className="video-issues-list">
					{issues.map((issue, index) => (
						<li key={index} className="p4">{issue}</li>
					))}
				</ul>
			);
		};
		
		return (
			<div className="behavior-grid">
				<div className="behavior-item">
					<div className="behavior-item__content">
						<span className="behavior-item__label p4">Clean Ad Experience</span>
						<div className="behavior-item__icon-container">
							<img 
								className="behavior-item__icon" 
								src={!hasIntrusiveAds ? checkmarkGreen : errorIcon}
								alt={!hasIntrusiveAds ? 'Good' : 'Warning'}
							/>
						</div>
						<span className="behavior-item__description p4">
							{getIntrusiveAdsDescription()}
						</span>
						{hasIntrusiveAds && (
							<>
								<button 
									className="intrusive-ads-toggle p4"
									onClick={() => setShowIntrusiveAds(!showIntrusiveAds)}
								>
									{showIntrusiveAds ? '▼ Hide' : '▶ View'} intrusive formats
								</button>
								{showIntrusiveAds && (
									<div className="intrusive-ads-section">
										{renderIntrusiveAdsList()}
									</div>
								)}
							</>
						)}
					</div>
				</div>
				<div className="behavior-item">
					<div className="behavior-item__content">
						<span className="behavior-item__label p4">Stable URL Structure</span>
						<div className="behavior-item__icon-container">
							<img 
								className="behavior-item__icon" 
								src={!results.dynamicUrlChangeDetected ? checkmarkGreen : errorIcon}
								alt={!results.dynamicUrlChangeDetected ? 'Good' : 'Warning'}
							/>
						</div>
						<span className="behavior-item__description p4">
							{getUrlChangeDescription()}
						</span>
						{results.dynamicUrlChangeDetected && results.urlChanges && results.urlChanges.length > 0 && (
							<>
								<button 
									className="url-changes-toggle p4"
									onClick={() => setShowUrlChanges(!showUrlChanges)}
								>
									{showUrlChanges ? '▼ Hide' : '▶ View'} URL changes
								</button>
								{showUrlChanges && (
									<div className="url-changes-section">
										{renderUrlChangesList()}
									</div>
								)}
							</>
						)}
					</div>
				</div>
				<div className="behavior-item">
					<div className="behavior-item__content">
						<span className="behavior-item__label p4">Standard Page Loading</span>
						<div className="behavior-item__icon-container">
							<img 
								className="behavior-item__icon" 
								src={!results.infiniteScrollDetected ? checkmarkGreen : errorIcon}
								alt={!results.infiniteScrollDetected ? 'Good' : 'Warning'}
							/>
						</div>
						<span className="behavior-item__description p4">
							{getScrollDescription()}
						</span>
					</div>
				</div>
				<div className="behavior-item">
					<div className="behavior-item__content">
						<span className="behavior-item__label p4">Video Ad Quality</span>
						<div className="behavior-item__icon-container">
							<img 
								className="behavior-item__icon" 
								src={!hasVideoIssues ? checkmarkGreen : errorIcon}
								alt={!hasVideoIssues ? 'Good' : 'Warning'}
							/>
						</div>
						<span className="behavior-item__description p4">
							{getVideoQualityDescription()}
						</span>
						{hasVideoIssues && (
							<>
								<button 
									className="video-issues-toggle p4"
									onClick={() => setShowVideoIssues(!showVideoIssues)}
								>
									{showVideoIssues ? '▼ Hide' : '▶ View'} issues
								</button>
								{showVideoIssues && (
									<div className="video-issues-section">
										{renderVideoIssuesList()}
									</div>
								)}
							</>
						)}
					</div>
				</div>
			</div>
		);
	};


	const renderDebugPanel = () => {
		if (!debugMode) return null;

		return (
			<div className="debug-panel">
				<div className="debug-header">
					<h3 className="p3 strong">Debug Information</h3>
					<button 
						className="debug-close-button"
						onClick={() => setDebugMode(false)}
					>
						Close Debug
					</button>
				</div>
				<div className="debug-content">
					{!rawResponse && !results && (
						<div className="debug-section">
							<div style={{ textAlign: 'center', padding: '2rem', color: '#f8f8f2' }}>
								<h4>No data available yet</h4>
								<p>Analyze a URL first to see debug information</p>
							</div>
						</div>
					)}
					{rawResponse && (
						<div className="debug-section">
							<h4 className="p4 strong">Raw Server Response:</h4>
							<pre className="debug-json">
								{JSON.stringify(rawResponse, null, 2)}
							</pre>
						</div>
					)}
					{results && (
						<div className="debug-section">
							<h4 className="p4 strong">Processed Results:</h4>
							<pre className="debug-json">
								{JSON.stringify(results, null, 2)}
							</pre>
						</div>
					)}
					<div className="debug-section">
						<h4 className="p4 strong">Analysis Metadata:</h4>
						<div className="debug-metadata">
							<div className="debug-item">
								<span className="p4 strong">Timestamp:</span>
								<span className="p4">{results?.timestamp ? new Date(results.timestamp).toISOString() : 'N/A'}</span>
							</div>
							<div className="debug-item">
								<span className="p4 strong">Source:</span>
								<span className="p4">{results?.source || 'N/A'}</span>
							</div>
							<div className="debug-item">
								<span className="p4 strong">URL Analyzed:</span>
								<span className="p4">{results?.url || 'N/A'}</span>
							</div>
							<div className="debug-item">
								<span className="p4 strong">Response Size:</span>
								<span className="p4">{rawResponse ? JSON.stringify(rawResponse).length : 0} characters</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	};


	return (
		<div id="ad-quality-analyzer" className="wrapper standalone">
			<div className="top-fold">
				<div className="container">
					<div className="container--inner">
						<a href="https://arcspan.com" target="_blank" rel="noopener noreferrer" className="logo-link">
							<img src={arcspanLogo} alt="ArcSpan" className="arcspan-logo" />
						</a>
						<div className="header-content">
							<div className="header-title-row">
								<div className="title-section">
									<h1 className="caps">Quality Cloud™ Analyzer</h1>
									<p className="p2">Comprehensive analysis of web page advertising quality and user experience to power curation and yield</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="container">
				<div className="container--inner">
					<form onSubmit={handleSubmit} className="analyzer-form">
						<div className="form-group">
							<label htmlFor="url-input" className="p3 strong">Enter URL to Analyze</label>
							<div className="input-row">
								<input
									id="url-input"
									type="text"
									value={url}
									onChange={(e) => setUrl(e.target.value)}
									placeholder="https://example.com"
									className="form-control"
									disabled={loading}
								/>
								<button 
									type="submit" 
									className="btn btn-primary"
									disabled={loading || !url}
								>
									{loading ? 'Analyzing...' : 'Analyze'}
								</button>
							</div>
						</div>
					</form>

					{error && (
						<div className="error-message">
							<p className="p3">{error}</p>
						</div>
					)}

					{loading && (
						<div className="loading-container">
							<Loader />
							<p className="p3">Performing comprehensive analysis... This typically takes a few minutes.</p>
						</div>
					)}

					{results && !loading && (
						<div className="results-container">
							{renderSummaryCard()}

							{/* Primary Metrics - Most Critical for Ad Quality */}
							<div className="metrics-grid primary-metrics">
								{renderAdRatioGauge()}
								{renderOutOfViewGauge()}
							</div>

							{/* Secondary Metrics - Supporting Information */}
							<div className="metrics-grid secondary-metrics">
								{renderAdSlotsGauge()}
								{renderNetworkRequestsGauge()}
								{renderTotalNetworkRequestsGauge()}
								{renderRefreshRateGauge()}
							</div>

							{/* Ad Quality Checks */}
							<div style={{ marginBottom: '2rem' }}>
								{renderAdQualityChecks()}
							</div>

						</div>
					)}
				</div>
			</div>
			
			<footer className="standalone-footer">
				<div className="container footer">
					<div className="footer--inner container--inner">
						<div className="footer--inner--text">
							©{new Date().getFullYear()} ArcSpan. All rights reserved.
						</div>
						<button 
							className="debug-toggle-button"
							onClick={() => setDebugMode(!debugMode)}
							title="Toggle debug overlay"
						>
							{debugMode ? 'Close Debug' : 'Debug Mode'}
						</button>
						{isTestMode && (
							<span 
								className="test-mode-indicator"
								title="Test mode active - video data is simulated"
							>
								TEST MODE: Video
							</span>
						)}
					</div>
				</div>
			</footer>
			
			{/* Debug Overlay */}
			{renderDebugPanel()}
		</div>
	);
}

export default AdQualityAnalyzer;